/**
 * Composant pour afficher les sessions
 */
import { SubpageHeader } from "@/components/global/SubpageHeader";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { WorkSession, LateSession } from "@/types/workSessions.type";
import {
  AlertTriangle,
  CalendarIcon,
  ChevronDown,
  ChevronUp,
  Clock,
  User,
  Edit2,
  MessageSquare,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { format, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import {
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Collapsible } from "@radix-ui/react-collapsible";
import { cn } from "@/lib/utils";
import { useSessions, useLateSessions, useUpdateComment } from "@/hooks/useSessions";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getPointeuseShowSessions } from "@/services/config.services";
import { Navigate } from "react-router-dom";

// ─── Carte employé réutilisable ───────────────────────────────────────────────
interface EmployeeCardProps {
  employeeName: string;
  employeeSessions: WorkSession[];
  isExpanded: boolean;
  onToggle: () => void;
  formatDuration: (h: number | null) => string;
  formatTime: (iso: string) => string;
  editingComment: string | null;
  commentValue: string;
  isUpdating: boolean;
  onEditOpen: (sessionId: string, current: string) => void;
  onEditClose: () => void;
  onCommentChange: (v: string) => void;
  onCommentSave: (sessionId: string, comment: string | null) => void;
  /** Affiche l'heure de fin prévue au planning au lieu de la durée totale */
  showMissedDate?: boolean;
  /** Heure de fin prévue au planning (ISO), utilisée avec showMissedDate */
  scheduledEndTime?: string;
  /** IDs des sessions ouvertes considérées en retard (planning dépassé) — sinon pointage en cours affiché en vert */
  lateSessionIds?: Set<string>;
}

const EmployeeCard = ({
  employeeName,
  employeeSessions,
  isExpanded,
  onToggle,
  formatDuration,
  formatTime,
  editingComment,
  commentValue,
  isUpdating,
  onEditOpen,
  onEditClose,
  onCommentChange,
  onCommentSave,
  showMissedDate = false,
  scheduledEndTime,
  lateSessionIds,
}: EmployeeCardProps) => {
  const totalDuration = employeeSessions.reduce(
    (acc, s) => acc + (s.duration || 0),
    0
  );
  const hasActiveSession = employeeSessions.some((s) => !s.endTime);
  const hasLateOpenSession = employeeSessions.some(
    (s) => !s.endTime && lateSessionIds?.has(s.id)
  );
  /** En cours sans alerte late → vert ; en cours + late (ou bannière) → orange */
  const activeIsAlert = showMissedDate || hasLateOpenSession;

  /** Heure seule si le shift se termine aujourd'hui, sinon date + heure (veille, etc.) */
  const formatScheduledClockOut = (iso: string) => {
    const d = new Date(iso);
    return isToday(d)
      ? format(d, "HH:mm", { locale: fr })
      : format(d, "dd MMM yyyy 'à' HH:mm", { locale: fr });
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className="bg-white/85 border-gray-200 backdrop-blur-sm rounded-xl border overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="w-full p-4 flex items-center gap-3 transition-colors hover:bg-[#D6DBDD]/85">
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center",
                !hasActiveSession && "bg-gray-100",
                hasActiveSession && activeIsAlert && "bg-orange-100",
                hasActiveSession && !activeIsAlert && "bg-green-100"
              )}
            >
              <User
                className={cn(
                  "w-5 h-5",
                  !hasActiveSession && "text-gray-600",
                  hasActiveSession && activeIsAlert && "text-orange-500",
                  hasActiveSession && !activeIsAlert && "text-green-600"
                )}
              />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-medium text-foreground">{employeeName}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  {employeeSessions.length} session
                  {employeeSessions.length > 1 ? "s" : ""}
                </span>
                <span>•</span>
                {showMissedDate ? (
                  <span className="font-medium text-orange-500">
                    devait dépointer
                    {scheduledEndTime
                      ? isToday(new Date(scheduledEndTime))
                        ? ` à ${formatScheduledClockOut(scheduledEndTime)}`
                        : ` le ${formatScheduledClockOut(scheduledEndTime)}`
                      : (() => {
                          const t = employeeSessions[0].startTime;
                          return isToday(new Date(t))
                            ? ` à ${formatScheduledClockOut(t)}`
                            : ` le ${formatScheduledClockOut(t)}`;
                        })()}
                  </span>
                ) : (
                  <span
                    className={cn(
                      "font-medium",
                      hasActiveSession && !activeIsAlert
                        ? "text-green-600"
                        : "text-primary"
                    )}
                  >
                    {totalDuration > 0
                      ? formatDuration(totalDuration)
                      : "En cours..."}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasActiveSession && (
                <span className="relative flex h-2 w-2">
                  {activeIsAlert ? (
                    <>
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
                    </>
                  ) : (
                    <>
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </>
                  )}
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-gray-200">
            {employeeSessions.map((session, idx) => (
              <div
                key={session.id}
                className={cn(
                  "px-4 py-3 flex items-center gap-4",
                  idx !== employeeSessions.length - 1 &&
                    "border-b border-gray-200"
                )}
              >
                <div className="w-10 flex justify-center">
                  <div className="flex flex-col items-center">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="w-0.5 h-4 bg-gray-300" />
                    <span
                      className={cn(
                        "w-2 h-2 rounded-full",
                        session.endTime
                          ? "bg-gray-400"
                          : lateSessionIds?.has(session.id) || showMissedDate
                            ? "bg-orange-400 animate-pulse"
                            : "bg-green-500 animate-pulse"
                      )}
                    />
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Arrivée</span>
                    <span className="font-medium text-gray-900">
                      {formatTime(session.startTime)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Départ</span>
                    <span className="font-medium text-gray-900">
                      {session.endTime ? formatTime(session.endTime) : "—"}
                    </span>
                  </div>
                  {session.comment && (
                    <div className="flex items-start gap-2 text-sm mt-2 pt-2 border-t border-gray-100">
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 italic">
                        {session.comment}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  {session.duration ? (
                    <span className="font-semibold text-primary">
                      {formatDuration(session.duration)}
                    </span>
                  ) : (
                    <span
                      className={cn(
                        "text-sm font-medium",
                        lateSessionIds?.has(session.id) || showMissedDate
                          ? "text-orange-500"
                          : "text-green-600"
                      )}
                    >
                      En cours
                    </span>
                  )}
                  <Popover
                    open={editingComment === session.id}
                    onOpenChange={(open) => {
                      if (!open) {
                        onEditClose();
                      } else {
                        onEditOpen(session.id, session.comment || "");
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <Edit2 className="h-4 w-4 text-gray-500" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="end">
                      <div className="space-y-3">
                        <Label htmlFor="comment">Commentaire</Label>
                        <Textarea
                          id="comment"
                          placeholder="Ex: Employé oublié de pointer à 10h, pointage à modifier..."
                          value={commentValue}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            onCommentChange(e.target.value)
                          }
                          rows={3}
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={onEditClose}
                          >
                            Annuler
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              onCommentSave(
                                session.id,
                                commentValue.trim() || null
                              )
                            }
                            disabled={isUpdating}
                          >
                            {isUpdating ? "Enregistrement..." : "Enregistrer"}
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
const SessionsContent = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());
  const [expandedActive, setExpandedActive] = useState<Set<string>>(new Set());
  const [alertOpen, setAlertOpen] = useState(true);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [commentValue, setCommentValue] = useState("");

  const { mutate: updateCommentMutation, isPending: isUpdating } = useUpdateComment();

  // Sessions du jour sélectionné
  const {
    data: sessions = [],
    isLoading,
    isError,
    error,
  } = useSessions(selectedDate);

  // Sessions dont l'heure de fin prévue au planning est dépassée
  const { data: lateSessions = [] } = useLateSessions();

  const lateSessionIds = useMemo(
    () => new Set(lateSessions.map((s) => s.id)),
    [lateSessions]
  );

  // Grouper les sessions du jour par employé
  const sessionsByEmployee = useMemo(() => {
    const grouped: Record<string, WorkSession[]> = {};
    sessions.forEach((session) => {
      const key = `${session.user.firstName} ${session.user.lastName}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(session);
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => {
        const diff = new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
        return diff !== 0 ? diff : a.id.localeCompare(b.id);
      });
    });
    return grouped;
  }, [sessions]);

  // Grouper les sessions late par employé
  const lateByEmployee = useMemo(() => {
    const grouped: Record<string, LateSession[]> = {};
    lateSessions.forEach((session) => {
      const key = `${session.user.firstName} ${session.user.lastName}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(session);
    });
    return grouped;
  }, [lateSessions]);

  const toggleEmployee = (name: string) =>
    setExpandedEmployees((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const toggleActive = (name: string) =>
    setExpandedActive((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const formatDuration = (hours: number | null): string => {
    if (!hours) return "En cours...";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h}h`;
    return `${h}h${m.toString().padStart(2, "0")}`;
  };

  const formatTime = (isoString: string): string =>
    new Date(isoString).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const totalHours = useMemo(
    () => sessions.reduce((acc, s) => acc + (s.duration || 0), 0),
    [sessions]
  );

  const handleUpdateComment = (sessionId: string, comment: string | null) => {
    updateCommentMutation(
      { id: sessionId, comment },
      {
        onSuccess: () => {
          setEditingComment(null);
          setCommentValue("");
        },
      }
    );
  };

  const sharedCardProps = {
    formatDuration,
    formatTime,
    editingComment,
    commentValue,
    isUpdating,
    onEditOpen: (id: string, current: string) => {
      setEditingComment(id);
      setCommentValue(current);
    },
    onEditClose: () => {
      setEditingComment(null);
      setCommentValue("");
    },
    onCommentChange: setCommentValue,
    onCommentSave: handleUpdateComment,
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50">
      <SubpageHeader
        pageTitle="Pointages"
        right={
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 bg-white/85 border-gray-300 hover:bg-[#D6DBDD]"
              >
                <CalendarIcon className="w-4 h-4" />
                {format(selectedDate, "dd MMM yyyy", { locale: fr })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                locale={fr}
              />
            </PopoverContent>
          </Popover>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[max(2.5rem,env(safe-area-inset-bottom))]">

          {/* ── Bannière : employés qui n'ont pas dépointé selon leur planning ── */}
          {lateSessions.length > 0 && (
            <Collapsible open={alertOpen} onOpenChange={setAlertOpen}>
              <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 overflow-hidden">
                <CollapsibleTrigger asChild>
                  <button className="w-full px-4 py-3 flex items-center gap-3 hover:bg-orange-100/60 transition-colors">
                    <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0" />
                    <span className="flex-1 text-left text-sm font-medium text-orange-700">
                      {Object.keys(lateByEmployee).length} employé
                      {Object.keys(lateByEmployee).length > 1 ? "s n'ont" : " n'a"} pas dépointé selon le planning
                    </span>
                    {alertOpen ? (
                      <ChevronUp className="w-4 h-4 text-orange-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-orange-500" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-4 pb-3 space-y-2">
                    {Object.entries(lateByEmployee).map(([name, empSessions]) => (
                      <EmployeeCard
                        key={name}
                        employeeName={name}
                        employeeSessions={empSessions}
                        isExpanded={expandedActive.has(name)}
                        onToggle={() => toggleActive(name)}
                        showMissedDate
                        scheduledEndTime={empSessions[0].scheduledEndTime}
                        lateSessionIds={lateSessionIds}
                        {...sharedCardProps}
                      />
                    ))}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* ── Résumé du jour ── */}
          <div className="mt-4">
            <div className="bg-white/80 border-gray-200 backdrop-blur-sm p-4 border rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-muted-foreground text-sm">
                    Total du jour
                  </span>
                  <p className="text-2xl font-bold text-primary">
                    {totalHours > 0 ? formatDuration(totalHours) : "0h"}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-muted-foreground text-sm">Employés</span>
                  <p className="text-2xl font-bold text-primary">
                    {Object.keys(sessionsByEmployee).length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Liste des sessions du jour ── */}
          <div className="mt-3 space-y-3 pb-2">
            {isLoading ? (
              <div className="text-center py-12 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50 animate-spin" />
                <p>Chargement des pointages...</p>
              </div>
            ) : isError ? (
              <div className="text-center py-12 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-red-600">
                  {error instanceof Error
                    ? error.message
                    : "Erreur lors du chargement"}
                </p>
              </div>
            ) : Object.keys(sessionsByEmployee).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun pointage pour cette date</p>
              </div>
            ) : (
              Object.entries(sessionsByEmployee).map(([name, empSessions]) => (
                <EmployeeCard
                  key={name}
                  employeeName={name}
                  employeeSessions={empSessions}
                  isExpanded={expandedEmployees.has(name)}
                  onToggle={() => toggleEmployee(name)}
                  lateSessionIds={lateSessionIds}
                  {...sharedCardProps}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Page liste des pointages : masquée si l'entreprise a désactivé l'affichage. */
export const Sessions = () => {
  if (!getPointeuseShowSessions()) {
    return <Navigate to="/home" replace />;
  }
  return <SessionsContent />;
};
